import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { ApiDigitalAuthInvitation } from '../../services/auth/invitation.service';
import { ApiDigitalAuthAuthentication } from '../../services/auth/authentication.service';
import { ApiDigitalAuthUser } from '../../services/auth/user.service';
import { Error } from '../../models/error.model';
import { TokenSwitch } from '../../models/token.model';
import { GlobalSwal } from '../../styles/swal.style';
import { authApiKey, authController, authRefresh, authSwitchCompany, authCompany } from '../../scripts/authentication.script';
import { getError } from '../../languages/translations/response';
import { errorWebHandler } from '../../scripts/error.web.handler.script';

export interface InvitationPageProps {};

let errorResponse: Error;

const InvitationPage: React.FunctionComponent<InvitationPageProps> = props => {
  const {lang} = StateLanguage()
  const param = useParams()
  const navigate = useNavigate()

  const [mounted, setMounted] = useState(false)

  const loadInvitationCompany = async (token: string) => {
    const apiKey = authApiKey()

    try {
      const response = await ApiDigitalAuthInvitation.postInvitationAccept(apiKey, token)

      if (response.status !== 200) {
        errorResponse = response.data
        await GlobalSwal.fire({
          title: getError(errorResponse?.code, lang.code, errorResponse?.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
        navigate(authController() ? '/main/dashboard' : '/auth/login', {replace: true})
        return
      }

      // Extract company ID from accept response (may include company_id or company)
      const acceptData = response.data
      const rawCompanyId = acceptData?.company_id?.id
        ?? (typeof acceptData?.company_id === 'string' ? acceptData.company_id : null)
        ?? acceptData?.company?.id
        ?? null
      let companyId: string | null = (typeof rawCompanyId === 'string' && rawCompanyId.length > 0) ? rawCompanyId : null

      // If accept response didn't include company ID, fetch companies list
      if (!companyId && authController()) {
        try {
          const companiesRes = await ApiDigitalAuthUser.getMeCompanies(apiKey)
          if (companiesRes.status === 200 && companiesRes.data) {
            const payload: any = companiesRes.data
            const rawList = Array.isArray(payload) ? payload : (payload?.companies ?? [])
            const current = rawList.find((c: any) => c.is_current === true)
            companyId = current?.id ?? current?._id ?? rawList[0]?.id ?? rawList[0]?._id ?? null
          }
        } catch {
          // Fall through — companyId stays null
        }
      }

      // Switch company to get a scoped JWT and write cookies
      if (companyId && authController()) {
        try {
          const switchRes = await ApiDigitalAuthAuthentication.switchCompany(apiKey, authRefresh(), companyId)
          if (switchRes.status === 200) {
            const tokenSwitch: TokenSwitch = switchRes.data
            authSwitchCompany(tokenSwitch)

            // Confirm the cookie was written with the correct company ID
            const writtenCompany = authCompany()
            if (!writtenCompany || writtenCompany.id !== companyId) {
              console.warn('[Invitation] Cookie company mismatch after switch:', writtenCompany?.id, '!==', companyId)
            }
          } else {
            console.warn('[Invitation] switchCompany failed, status:', switchRes.status)
          }
        } catch (switchErr) {
          console.warn('[Invitation] switchCompany error:', switchErr)
        }
      }

      await GlobalSwal.fire({
        title: lang.labels.successfullyAcceptedInvitation,
        text: lang.labels.validTokenAutomaticLoginRedirectedDashboard,
        icon: 'success',
        showConfirmButton: false,
        timer: 2200
      })
      navigate(authController() ? '/main/dashboard' : '/auth/login', {replace: true})

    } catch (error) {
      errorWebHandler(lang, error)
    }
  }

  const executeReloadPage = () => {
    navigate(0)
  }

  useEffect( () => {
    setMounted(true)

    if (param.token) {
      loadInvitationCompany(param.token)
    }

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="py-10">
      <h1 className="fs-3qx text-dark text-capitalize mb-5">{lang.labels.acceptingInvitationToAccessCompany}</h1>
      <div className="fs-3 fw-bold text-muted mb-10">
        {lang.labels.heWantsAccessApplicationLink},
        <br />
        {lang.labels.waitAMomentToVerifyYourAccess}
      </div>
      <div className="text-center my-10">
        <section className="global">
          <div className="global-top mask">
            <div className="plane"></div>
          </div>
          <div className="global-middle mask">
            <div className="plane"></div>
          </div>
          <div className="global-bottom mask">
            <div className="plane"></div>
          </div>
          <p className="global-text">
            <i>{lang.labels.loading}</i>
          </p>
        </section>
      </div>
      <div className="d-flex justify-content-center fs-5">
        <span className="fw-bold text-secondary">{lang.labels.doesItTakeALongTimeToLoadTheApp}</span>
        <span className="btn btn-link link-primary p-0 ms-1" onClick={executeReloadPage}>{lang.labels.reload}</span>
      </div>
    </div>
  )
};

export default InvitationPage;
